/**
 * Single calculation context for Stages 1–4 (Pascal-aligned pipeline).
 *
 * water + crop targets + salts → one matrix solve → Result[] used everywhere.
 */

import {
  getFormulationTargets,
  getSaltComposition,
  normalizeElementSymbol,
} from './saltElements';
import { normalizePurity } from './UnifiedHydroBuddyEngine';

export interface ContextElement {
  symbol: string;
  concentration: number;
  unit?: string;
}

export interface ContextSalt {
  id: string;
  name: string;
  formula: string;
  purity: number;
  isLiquid: boolean;
  solubility?: number;
  density?: number;
  elements: { symbol: string; percentage: number }[];
}

export interface CalculationContext {
  water: {
    id?: string;
    name: string;
    ph: number;
    ec: number;
    elements: ContextElement[];
  };
  formulation: {
    id?: string;
    name: string;
    cropType?: string;
    targetEc?: number;
    targetPh?: number;
    elements: ContextElement[];
  };
  salts: ContextSalt[];
  stockVolumeL: number;
  concentrationFactor: number;
  degreeOfFreedomElement: string;
  temperatureC: number;
}

/** Build engine/report-ready context from workflow Redux pieces. */
export function buildCalculationContext(args: {
  waterProfile: any;
  formulation: any;
  saltSelection: any;
  stockVolume?: number;
  concentrationFactor?: number;
  degreeOfFreedomElement?: string;
  temperatureC?: number;
}): CalculationContext {
  const waterElements: ContextElement[] = (
    args.waterProfile?.elements || []
  )
    .map((e: any) => ({
      symbol: String(
        e?.symbol || e?.elementSymbol || e?.element?.symbol || ''
      ).trim(),
      concentration: Number(e?.concentration ?? e?.value ?? 0) || 0,
      unit: e?.unit || 'ppm',
    }))
    .filter((e: ContextElement) => e.symbol && e.concentration > 0);

  const formulationElements: ContextElement[] = getFormulationTargets(
    args.formulation
  )
    .map((t) => ({
      symbol: t.symbol,
      concentration: Number(t.concentration) || 0,
      unit: 'ppm',
    }))
    .filter((e) => e.symbol && e.concentration > 0);

  const salts: ContextSalt[] = (args.saltSelection?.selectedSalts || []).map(
    (salt: any) => {
      const composition = getSaltComposition(salt);
      return {
        id: salt.id,
        name: salt.name,
        formula: salt.formula || '',
        purity: normalizePurity(salt.purity ?? 1),
        isLiquid: Boolean(salt.isLiquid),
        solubility: salt.solubility,
        density: salt.density,
        elements: composition.map((el) => ({
          symbol: el.symbol,
          percentage: el.percentage,
        })),
      };
    }
  );

  return {
    water: {
      id: args.waterProfile?.id,
      name: args.waterProfile?.name || 'Unknown water',
      ph: Number(args.waterProfile?.ph) || 7,
      ec: Number(args.waterProfile?.ec) || 0,
      elements: waterElements,
    },
    formulation: {
      id: args.formulation?.id,
      name: args.formulation?.name || 'Unknown crop',
      cropType: args.formulation?.cropType,
      targetEc: args.formulation?.targetEc,
      targetPh: args.formulation?.targetPh,
      elements: formulationElements,
    },
    salts: salts.filter((s) => s.elements.length > 0),
    stockVolumeL: Math.max(0.001, Number(args.stockVolume) || 1),
    concentrationFactor: Math.max(1, Number(args.concentrationFactor) || 1),
    degreeOfFreedomElement: args.degreeOfFreedomElement || 'S',
    temperatureC: Number(args.temperatureC) || 20,
  };
}

/** Targets map for stock calculator / engine. */
export function contextToTargetMap(
  ctx: CalculationContext
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const e of ctx.formulation.elements) {
    if (e.symbol && e.concentration > 0) {
      map[e.symbol] = e.concentration;
    }
  }
  return map;
}

/**
 * Working-solution ppm from stock salt grams (Pascal chemistry).
 * stock_g / CF = working grams for same tank volume after dilution.
 */
export function contributionPpmWorking(
  saltGramsStock: number,
  elementPercentage: number,
  purity: number,
  volumeL: number,
  concentrationFactor: number
): number {
  if (!(volumeL > 0) || !(saltGramsStock > 0) || !(elementPercentage > 0)) {
    return 0;
  }
  const cf = Math.max(1, concentrationFactor);
  const workingG = saltGramsStock / cf;
  const p = normalizePurity(purity);
  // ppm = g × % × purity_frac × 10 / V_L
  return (workingG * elementPercentage * p * 10) / volumeL;
}

/** Lookup water background for a formulation symbol (normalized match). */
export function waterBackgroundForSymbol(
  waterElements: ContextElement[],
  symbol: string
): number {
  const want = normalizeElementSymbol(symbol);
  let sum = 0;
  for (const w of waterElements) {
    if (normalizeElementSymbol(w.symbol) === want) {
      sum += w.concentration;
    }
  }
  return sum;
}
